import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Loader2, CreditCard, Truck, Timer, XCircle, MessageCircle, MapPin } from "lucide-react";
import { useSeoMeta, useLanguage } from "../i18n/LanguageContext";
import { translations } from "../i18n/translations";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const WHATSAPP_NUMBER = "201037255878";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_SHIP = { full_name: "", email: "", address: "", city: "", country: "", zip: "", phone: "" };

export default function BrandedPaymentPage() {
  const { ref } = useParams();
  const { t, setLang } = useLanguage();
  const [state, setState] = useState({ loading: true, invoice: null, error: "" });
  const [ship, setShip] = useState(EMPTY_SHIP);
  const [shipError, setShipError] = useState("");
  const [paying, setPaying] = useState(false);
  const [shipDone, setShipDone] = useState(false);
  useSeoMeta({
    title: "Locker Room | Secure Payment",
    description: "Secure payment and shipping details for your Locker Room order.",
    noindex: true,
  });

  useEffect(() => {
    if (!ref) return setState({ loading: false, invoice: null, error: "missing-ref" });
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payment/status/${ref}`);
        setState({ loading: false, invoice: data, error: "" });
        // Open the page in the same language the customer ordered in (fallback: English)
        const orderLang = data.lang;
        if (orderLang && translations[orderLang]) setLang(orderLang);
        else if (orderLang !== null && orderLang !== undefined) setLang("en");
        // Prefill shipping form with previously saved data (if any)
        setShip({
          full_name: data.shipping_name || data.customer_name || "",
          email: data.shipping_email || "",
          address: data.shipping_address || "",
          city: data.shipping_city || "",
          country: data.shipping_country || "",
          zip: data.shipping_zip || "",
          phone: data.shipping_phone || "",
        });
      } catch {
        setState({ loading: false, invoice: null, error: "not-found" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const setField = (k) => (e) => setShip((s) => ({ ...s, [k]: e.target.value }));

  // Saves shipping details to our DB (invoice + linked order). Stripe never receives them.
  // Returns true on success.
  const submitShipping = async () => {
    if (["full_name", "address", "city", "country", "zip"].some((k) => !ship[k].trim())) {
      setShipError(t("pay.fillAll"));
      return false;
    }
    if (!EMAIL_RE.test(ship.email.trim())) {
      setShipError(t("pay.invalidEmail"));
      return false;
    }
    setShipError("");
    setPaying(true);
    try {
      await axios.post(`${API}/payment/shipping/${ref}`, {
        full_name: ship.full_name.trim(),
        email: ship.email.trim(),
        address: ship.address.trim(),
        city: ship.city.trim(),
        country: ship.country.trim(),
        zip: ship.zip.trim(),
        phone: ship.phone.trim() || null,
      });
    } catch (e) {
      setPaying(false);
      setShipError(
        e?.response?.status === 410
          ? t("pay.expiredError")
          : e?.response?.data?.detail === "Invalid email address"
            ? t("pay.invalidEmail")
            : t("pay.payError")
      );
      return false;
    }
    return true;
  };

  // Full link: save shipping, then redirect to Stripe Checkout
  const handlePay = async () => {
    const url = state.invoice?.hosted_url || state.invoice?.tap_url;
    if (!url) return;
    if (await submitShipping()) window.location.href = url;
  };

  // Shipping-only link: save shipping and show confirmation (payment handled separately by the store)
  const handleShippingConfirm = async () => {
    if (await submitShipping()) {
      setPaying(false);
      setShipDone(true);
    }
  };

  const inv = state.invoice;
  const isPaid = inv && (inv.status === "PAID" || inv.status === "CAPTURED");
  const isExpired = inv && !isPaid && inv.link_expired;
  const isShippingOnly = inv && inv.link_type === "shipping_only";
  const waNewLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Ciao Locker Room! Il mio link (${ref}) è scaduto. Potete inviarmene uno nuovo?`
  )}`;
  const expiresLabel = inv?.expires_at
    ? new Date(inv.expires_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;

  const labelCls = "block text-sm md:text-base font-bold tracking-[0.1em] uppercase text-white mb-2 font-body";
  const inputCls = "w-full px-4 py-3.5 bg-black/40 border border-white/10 rounded-sm text-white font-body text-base focus:outline-none focus:border-[#009A49] transition-colors placeholder:text-zinc-500";

  const Field = ({ label, fieldKey, type = "text", placeholder, testid, required = true }) => (
    <div>
      <label className={labelCls}>
        {label}
        {!required && <span className="ml-2 text-[11px] tracking-normal normal-case text-zinc-500 font-body">· {t("pay.optional")}</span>}
        {required && <span className="ml-1.5 text-[#009A49]">*</span>}
      </label>
      <input type={type} value={ship[fieldKey]} onChange={setField(fieldKey)} placeholder={placeholder} className={inputCls} data-testid={testid} />
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Background image with heavy overlay */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1551854415-1df5f0d94b99?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.25,
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
      </div>

      {/* Header */}
      <header className="pt-8 pb-6 px-6 text-center">
        <Link to="/" className="inline-flex flex-col items-center gap-3">
          <img src="/lr-monogram.svg" alt="Locker Room – boutique premium di maglie sportive" className="h-16 md:h-20 w-auto" />
          <div className="font-heading text-2xl md:text-3xl font-bold tracking-[0.15em]">
            <span className="text-[#009A49]">LOCKER</span>{" "}
            <span className="text-white">ROOM</span>
          </div>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-lg">
          {state.loading && (
            <div className="bg-[#0A0A0A]/90 border border-white/10 backdrop-blur-md rounded-sm p-10 text-center">
              <Loader2 className="w-12 h-12 text-[#009A49] mx-auto animate-spin mb-4" />
              <p className="text-zinc-400 text-sm">{t("pay.loading")}</p>
            </div>
          )}

          {!state.loading && state.error && (
            <div className="bg-[#0A0A0A]/90 border border-white/10 backdrop-blur-md rounded-sm p-10 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-bold mb-2">{t("pay.notFoundTitle")}</h1>
              <Link to="/" className="text-[#009A49] hover:underline text-xs tracking-[0.2em] uppercase font-body">{t("pay.backToSite")}</Link>
            </div>
          )}

          {!state.loading && inv && (
            <>
              <p className="text-center text-xs tracking-[0.4em] uppercase text-[#009A49] mb-6 font-body">
                {isShippingOnly ? t("pay.confirmShippingEyebrow") : t("pay.securePayment")}
              </p>

              <div className="bg-[#0A0A0A]/90 border-2 border-[#009A49] backdrop-blur-md rounded-sm overflow-hidden shadow-2xl shadow-[#009A49]/20" data-testid="branded-payment-card">
                {/* Amount — hidden for shipping-only links */}
                {!isShippingOnly && (
                  <div className="text-center px-8 pt-8 pb-6 border-b border-white/10">
                    <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-3 font-body">{t("pay.totalToPay")}</p>
                    <div className="font-heading text-6xl md:text-7xl font-bold text-white leading-none" data-testid="branded-amount">
                      €{(inv.total_amount ?? inv.amount ?? 0).toFixed(2)}
                    </div>
                  </div>
                )}

                {/* Line items */}
                <div className="px-8 py-6 space-y-4 border-b border-white/10">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body mb-2">{t("pay.orderDetails")}</p>
                  <div className="flex justify-between items-start gap-4 pb-3 border-b border-white/5">
                    <div>
                      <p className="text-white font-body font-semibold">{inv.description}</p>
                      <p className="text-zinc-500 text-xs mt-1 font-mono">Rif. {inv.client_ref}</p>
                    </div>
                    {!isShippingOnly && (
                      <p className="text-white font-mono whitespace-nowrap">€{(inv.base_amount ?? inv.amount ?? 0).toFixed(2)}</p>
                    )}
                  </div>
                  {!isShippingOnly && (
                    <>
                      <div className="flex justify-between items-start gap-4 pb-3 border-b border-white/5">
                        <div>
                          <p className="text-zinc-300 font-body">{t("pay.serviceFee")}</p>
                          <p className="text-zinc-500 text-xs mt-1">{inv.service_fee_percent ?? 3.5}% {t("pay.ofProduct")}</p>
                        </div>
                        <p className="text-zinc-300 font-mono whitespace-nowrap">€{(inv.service_fee ?? 0).toFixed(2)}</p>
                      </div>
                      <div className="flex justify-between items-center pt-1">
                        <p className="text-white font-body font-bold text-sm tracking-wider uppercase">{t("pay.total")}</p>
                        <p className="text-[#009A49] font-mono font-bold text-xl">€{(inv.total_amount ?? inv.amount ?? 0).toFixed(2)}</p>
                      </div>
                    </>
                  )}
                  {inv.customer_name && (
                    <div className="flex justify-between text-xs pt-2 border-t border-white/5">
                      <span className="text-zinc-500 font-body">{t("pay.billedTo")}</span>
                      <span className="text-white">{inv.customer_name}</span>
                    </div>
                  )}
                </div>

                {/* Payment methods — hidden for shipping-only links */}
                {!isShippingOnly && (
                  <div className="px-8 py-6 border-b border-white/10">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3 font-body">{t("pay.acceptedMethods")}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-3 py-1.5 border border-white/10 rounded-full text-[11px] font-body text-zinc-300 tracking-wide">VISA</span>
                      <span className="px-3 py-1.5 border border-white/10 rounded-full text-[11px] font-body text-zinc-300 tracking-wide">Mastercard</span>
                      <span className="px-3 py-1.5 border border-white/10 rounded-full text-[11px] font-body text-zinc-300 tracking-wide">Apple Pay</span>
                      <span className="px-3 py-1.5 border border-white/10 rounded-full text-[11px] font-body text-zinc-300 tracking-wide">Google Pay</span>
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="p-6 md:p-8">
                  {isPaid ? (
                    <div className="bg-[#009A49]/20 border border-[#009A49]/40 rounded-sm p-4 text-center text-[#009A49] text-sm font-semibold" data-testid="payment-already-paid">
                      {t("pay.alreadyPaid")}
                    </div>
                  ) : isExpired ? (
                    <div data-testid="link-expired-message">
                      <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-5 mb-4 text-center">
                        <Timer className="w-8 h-8 text-red-400 mx-auto mb-2" />
                        <p className="text-red-300 font-body font-semibold text-sm mb-1">{t("pay.expiredTitle")}</p>
                        <p className="text-zinc-400 text-xs font-body leading-relaxed">{t("pay.expiredText")}</p>
                      </div>
                      <a
                        href={waNewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-[#25D366] hover:bg-[#1eb85a] text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-4 rounded-full transition-colors flex items-center justify-center gap-3"
                        data-testid="request-new-link-btn"
                      >
                        <MessageCircle className="w-5 h-5" />
                        {t("pay.requestNewLink")}
                      </a>
                    </div>
                  ) : isShippingOnly && shipDone ? (
                    <div className="bg-[#009A49]/20 border border-[#009A49]/40 rounded-sm p-5 text-center" data-testid="shipping-confirmed">
                      <p className="text-[#009A49] text-sm font-semibold font-body mb-1">{t("pay.confirmedTitle")}</p>
                      <p className="text-zinc-400 text-xs font-body leading-relaxed">{t("pay.confirmedText")}</p>
                    </div>
                  ) : (
                    <>
                      {/* Shipping details — saved to Locker Room DB only, never sent to Stripe */}
                      <div className="mb-6" data-testid="shipping-form">
                        <p className="text-xs tracking-[0.3em] uppercase text-zinc-400 font-body mb-2 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#009A49]" />
                          {t("pay.shippingTitle")}
                        </p>
                        <p className="text-zinc-500 text-xs font-body mb-6 leading-relaxed">
                          {isShippingOnly ? t("pay.shippingNoteOnly") : t("pay.shippingNote")}
                        </p>
                        <div className="space-y-5">
                          <Field label={t("pay.fullName")} fieldKey="full_name" placeholder={t("pay.fullNamePh")} testid="shipping-fullname-input" />
                          <Field label={t("pay.email")} fieldKey="email" type="email" placeholder={t("pay.emailPh")} testid="shipping-email-input" />
                          <Field label={t("pay.address")} fieldKey="address" placeholder={t("pay.addressPh")} testid="shipping-address-input" />
                          <div className="grid grid-cols-2 gap-4">
                            <Field label={t("pay.city")} fieldKey="city" placeholder={t("pay.city")} testid="shipping-city-input" />
                            <Field label={t("pay.zip")} fieldKey="zip" placeholder={t("pay.zip")} testid="shipping-zip-input" />
                          </div>
                          <Field label={t("pay.country")} fieldKey="country" placeholder={t("pay.country")} testid="shipping-country-input" />
                          <Field label={t("pay.phone")} fieldKey="phone" type="tel" placeholder={t("pay.phonePh")} testid="shipping-phone-input" required={false} />
                        </div>
                        {inv.shipping_saved_at && (
                          <p className="text-[#009A49] text-xs font-body mt-4" data-testid="shipping-saved-note">{t("pay.shippingSavedNote")}</p>
                        )}
                      </div>

                      {shipError && <p className="text-red-400 font-body text-sm mb-4" data-testid="shipping-error">{shipError}</p>}

                      {isShippingOnly ? (
                        <button
                          onClick={handleShippingConfirm}
                          disabled={paying}
                          className="w-full bg-[#009A49] hover:bg-[#00b356] disabled:opacity-70 text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-5 rounded-full transition-colors flex items-center justify-center gap-3 shadow-lg shadow-[#009A49]/40 hover:shadow-[#009A49]/60"
                          data-testid="confirm-shipping-btn"
                        >
                          {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Truck className="w-5 h-5" />}
                          {paying ? t("pay.saving") : t("pay.confirmShippingBtn")}
                        </button>
                      ) : inv.hosted_url || inv.tap_url ? (
                        <button
                          onClick={handlePay}
                          disabled={paying}
                          className="w-full bg-[#009A49] hover:bg-[#00b356] disabled:opacity-70 text-white font-body font-bold text-sm tracking-[0.2em] uppercase py-5 rounded-full transition-colors flex items-center justify-center gap-3 shadow-lg shadow-[#009A49]/40 hover:shadow-[#009A49]/60"
                          data-testid="proceed-to-payment-btn"
                        >
                          {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                          {paying ? t("pay.saving") : t("pay.proceedToPayment")}
                        </button>
                      ) : (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 text-center text-amber-300 text-xs font-body">
                          {t("pay.checkoutPending")}
                        </div>
                      )}
                      {expiresLabel && (
                        <p className="flex items-center justify-center gap-1.5 text-zinc-600 text-[10px] mt-3 font-body" data-testid="link-expiry-hint">
                          <Timer className="w-3 h-3" />
                          {t("pay.linkValidUntil")} {expiresLabel}
                        </p>
                      )}
                    </>
                  )}
                  {!isShippingOnly && (
                    <p className="flex items-center justify-center gap-2 text-zinc-500 text-[11px] mt-4 font-body">
                      <ShieldCheck className="w-4 h-4 text-[#009A49]" />
                      {t("pay.stripeProtected")}
                    </p>
                  )}
                </div>
              </div>

              {/* Shipping perks */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="bg-[#0A0A0A]/60 backdrop-blur-md border border-white/5 rounded-sm p-4 flex items-center gap-3">
                  <Truck className="w-5 h-5 text-[#009A49] flex-shrink-0" />
                  <p className="text-[11px] text-zinc-400 leading-tight">{t("pay.freeShipping")}</p>
                </div>
                <div className="bg-[#0A0A0A]/60 backdrop-blur-md border border-white/5 rounded-sm p-4 flex items-center gap-3">
                  <Timer className="w-5 h-5 text-[#009A49] flex-shrink-0" />
                  <p className="text-[11px] text-zinc-400 leading-tight">{t("pay.delivery")}</p>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </main>

      <footer className="py-6 text-center text-zinc-600 text-[10px] tracking-[0.3em] uppercase font-body">
        © Locker Room · Marina Port Ghalib
      </footer>
    </div>
  );
}
